import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getCashRegisters,
  getCashRegisterSessions,
  getCashRegisterEvents,
} from "../api";
import DateRangePresetPicker from "../components/DateRangePresetPicker";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "../components/InlineLoading";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return `$${numeric.toLocaleString("de-DE")}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-CL");
};

const formatUser = (user) => {
  if (!user) return "-";
  return user.name || user.username || `Usuario ${user.id}`;
};

const eventLabel = (type) => {
  if (type === "open") return "Apertura";
  if (type === "close") return "Cierre";
  if (type === "cancel") return "Cancelacion";
  if (type === "suspend_sale") return "Venta suspendida";
  if (type === "resume_sale") return "Venta reanudada";
  if (type === "convert_to_quotation") return "Convertida a cotizacion";
  return type || "-";
};

const renderDetailRow = (label, value) => (
  <div className="flex justify-between gap-2 text-xs text-gray-600">
    <span className="font-medium text-gray-500">{label}</span>
    <span className="text-gray-700">{value || "-"}</span>
  </div>
);

const renderEventDetail = (event) => {
  const metadata = event?.metadata || {};
  if (event?.eventType === "open") {
    return (
      <div className="space-y-1">
        {renderDetailRow("Efectivo apertura", formatCurrency(metadata.openingCash))}
      </div>
    );
  }
  if (event?.eventType === "close") {
    return (
      <div className="space-y-1">
        {renderDetailRow("Efectivo cierre", formatCurrency(metadata.closingCash))}
        {renderDetailRow("Esperado", formatCurrency(metadata.expectedCash))}
        {renderDetailRow("Diferencia", formatCurrency(metadata.cashDifference))}
        {metadata.closeReason && renderDetailRow("Motivo", metadata.closeReason)}
      </div>
    );
  }
  if (event?.eventType === "cancel") {
    return (
      <div className="space-y-1">
        {renderDetailRow("Venta", metadata.purchaseId ? `#${metadata.purchaseId}` : "-")}
        {metadata.reason && renderDetailRow("Motivo", metadata.reason)}
      </div>
    );
  }
  if (event?.eventType === "suspend_sale") {
    return (
      <div className="space-y-1">
        {renderDetailRow("Venta", metadata.purchaseId ? `#${metadata.purchaseId}` : "-")}
        {metadata.suspendedCode && renderDetailRow("Codigo", metadata.suspendedCode)}
      </div>
    );
  }
  if (event?.eventType === "resume_sale") {
    return (
      <div className="space-y-1">
        {renderDetailRow("Venta", metadata.purchaseId ? `#${metadata.purchaseId}` : "-")}
        {metadata.suspendedCode && renderDetailRow("Codigo", metadata.suspendedCode)}
      </div>
    );
  }
  if (event?.eventType === "convert_to_quotation") {
    return (
      <div className="space-y-1">
        {renderDetailRow("Venta", metadata.purchaseId ? `#${metadata.purchaseId}` : "-")}
        {renderDetailRow("Cotizacion", metadata.quotationId ? `#${metadata.quotationId}` : "-")}
      </div>
    );
  }

  const entries = Object.entries(metadata || {});
  if (!entries.length) {
    return <span className="text-xs text-gray-500">Sin detalle</span>;
  }
  return (
    <div className="space-y-1">
      {entries.map(([key, value]) => renderDetailRow(key, String(value)))}
    </div>
  );
};

const CashRegisterLogsPage = () => {
  const params = useParams();
  const branchId = params.branchId || localStorage.getItem("selectedBranchId");
  const [view, setView] = useState("sessions");
  const [cashRegisters, setCashRegisters] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    cashRegisterId: "",
    eventType: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    if (!branchId) return;
    const loadRegisters = async () => {
      try {
        const result = await getCashRegisters(branchId, { pageSize: 0 });
        const normalized = normalizePaginatedResponse(result);
        setCashRegisters(normalized.data || []);
      } catch (err) {
        setError(err.message || "Error cargando cajas.");
      }
    };
    loadRegisters();
  }, [branchId]);

  const cashRegisterOptions = useMemo(() => {
    return (cashRegisters || []).map((register) => ({
      id: register.id,
      label: register.name || `Caja ${register.id}`,
    }));
  }, [cashRegisters]);

  useEffect(() => {
    if (!branchId) return;
    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        if (view === "sessions") {
          const result = await getCashRegisterSessions(branchId, {
            startDate: filters.startDate,
            endDate: filters.endDate,
            cashRegisterId: filters.cashRegisterId,
            page,
            pageSize,
          });
          const normalized = normalizePaginatedResponse(result);
          setSessions(normalized.data || []);
          setTotalItems(normalized.total || 0);
          setTotalPages(
            normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / pageSize))
          );
        } else {
          const result = await getCashRegisterEvents(branchId, {
            startDate: filters.startDate,
            endDate: filters.endDate,
            cashRegisterId: filters.cashRegisterId,
            eventType: filters.eventType,
            page,
            pageSize,
          });
          const normalized = normalizePaginatedResponse(result);
          setEvents(normalized.data || []);
          setTotalItems(normalized.total || 0);
          setTotalPages(
            normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / pageSize))
          );
        }
      } catch (err) {
        setError(err.message || "Error cargando logs.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [branchId, view, filters, page]);

  useEffect(() => {
    setPage(1);
  }, [view, filters.startDate, filters.endDate, filters.cashRegisterId, filters.eventType]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFilters({
      startDate: "",
      endDate: "",
      cashRegisterId: "",
      eventType: "",
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Logs de caja</h1>
          <p className="text-sm text-gray-600">
            Historial de sesiones y eventos por caja.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className={`px-3 py-1 rounded text-sm ${
              view === "sessions" ? "bg-blue-500 text-white" : "bg-gray-200"
            }`}
            onClick={() => setView("sessions")}
          >
            Sesiones
          </button>
          <button
            className={`px-3 py-1 rounded text-sm ${
              view === "events" ? "bg-blue-500 text-white" : "bg-gray-200"
            }`}
            onClick={() => setView("events")}
          >
            Eventos
          </button>
        </div>
      </div>

      <div className="bg-white rounded shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <DateRangePresetPicker
            label="Rango de fechas"
            presetLabel="Rango rapido"
            startLabel="Desde"
            endLabel="Hasta"
            startDate={filters.startDate}
            endDate={filters.endDate}
            onStartDateChange={(value) => handleFilterChange("startDate", value)}
            onEndDateChange={(value) => handleFilterChange("endDate", value)}
            containerClassName="md:col-span-2"
            labelClassName="block text-xs text-gray-600 mb-1"
            inputLabelClassName="block text-xs text-gray-600 mb-1"
            selectClassName="w-full border rounded p-2 text-sm"
            inputClassName="w-full border rounded p-2 text-sm"
          />
          <div>
            <label className="block text-xs text-gray-600">Caja</label>
            <select
              value={filters.cashRegisterId}
              onChange={(e) => handleFilterChange("cashRegisterId", e.target.value)}
              className="w-full border rounded p-2 text-sm"
            >
              <option value="">Todas</option>
              {cashRegisterOptions.map((register) => (
                <option key={register.id} value={register.id}>
                  {register.label}
                </option>
              ))}
            </select>
          </div>
          {view === "events" ? (
            <div>
              <label className="block text-xs text-gray-600">Evento</label>
                <select
                  value={filters.eventType}
                  onChange={(e) => handleFilterChange("eventType", e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                >
                  <option value="">Todos</option>
                  <option value="open">Apertura</option>
                  <option value="close">Cierre</option>
                  <option value="cancel">Cancelacion</option>
                  <option value="suspend_sale">Venta suspendida</option>
                  <option value="resume_sale">Venta reanudada</option>
                  <option value="convert_to_quotation">Convertida a cotizacion</option>
                </select>
            </div>
          ) : (
            <div className="flex items-end">
              <button
                type="button"
                className="w-full bg-gray-200 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-300"
                onClick={handleReset}
              >
                Restablecer
              </button>
            </div>
          )}
        </div>
        {view === "events" && (
          <div className="flex justify-end mt-3">
            <button
              type="button"
              className="bg-gray-200 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-300"
              onClick={handleReset}
            >
              Restablecer
            </button>
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-600 mb-4">{error}</div>}

      {loading ? (
        <div className="p-4">
          <InlineLoading />
        </div>
      ) : view === "sessions" ? (
        <div className="bg-white rounded shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-3">Caja</th>
                <th className="text-left p-3">Apertura</th>
                <th className="text-left p-3">Cierre</th>
                <th className="text-left p-3">Usuario</th>
                <th className="text-left p-3">Efectivo</th>
                <th className="text-left p-3">Diferencia</th>
                <th className="text-left p-3">Estado</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => {
                const diff = Number(session.cashDifference || 0);
                const diffLabel = formatCurrency(session.cashDifference);
                const diffClass =
                  diff === 0
                    ? "text-gray-700"
                    : diff > 0
                      ? "text-green-600"
                      : "text-red-600";
                return (
                  <tr key={session.id} className="border-t">
                    <td className="p-3">{session.CashRegister?.name || `Caja ${session.cashRegisterId}`}</td>
                    <td className="p-3">
                      <div>{formatDateTime(session.openedAt)}</div>
                      <div className="text-xs text-gray-500">
                        {formatUser(session.OpenedByUser)}
                      </div>
                    </td>
                    <td className="p-3">
                      <div>{formatDateTime(session.closedAt)}</div>
                      <div className="text-xs text-gray-500">
                        {formatUser(session.ClosedByUser)}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-xs text-gray-500">Aperturo</div>
                      <div>{formatUser(session.OpenedByUser)}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-xs text-gray-500">Inicial</div>
                      <div>{formatCurrency(session.openingCash)}</div>
                      <div className="text-xs text-gray-500 mt-1">Esperado</div>
                      <div>{formatCurrency(session.expectedCash)}</div>
                      <div className="text-xs text-gray-500 mt-1">Cierre</div>
                      <div>{formatCurrency(session.closingCash)}</div>
                    </td>
                    <td className={`p-3 font-semibold ${diffClass}`}>
                      {diffLabel}
                      {session.closeReason && (
                        <div className="text-xs text-gray-500 mt-1">
                          Motivo: {session.closeReason}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        session.status === "open" ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-700"
                      }`}>
                        {session.status === "open" ? "Abierta" : "Cerrada"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!sessions.length && (
                <tr>
                  <td className="p-4 text-gray-500" colSpan={7}>
                    No hay sesiones en este rango.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-3">Fecha</th>
                <th className="text-left p-3">Caja</th>
                <th className="text-left p-3">Evento</th>
                <th className="text-left p-3">Usuario</th>
                <th className="text-left p-3">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                return (
                  <tr key={event.id} className="border-t">
                    <td className="p-3">{formatDateTime(event.createdAt)}</td>
                    <td className="p-3">{event.CashRegister?.name || `Caja ${event.cashRegisterId}`}</td>
                    <td className="p-3">{eventLabel(event.eventType)}</td>
                    <td className="p-3">{formatUser(event.User)}</td>
                    <td className="p-3">{renderEventDetail(event)}</td>
                  </tr>
                );
              })}
              {!events.length && (
                <tr>
                  <td className="p-4 text-gray-500" colSpan={5}>
                    No hay eventos en este rango.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalItems > pageSize && !loading && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500 mt-4">
          <div>
            Mostrando {totalItems === 0 ? 0 : (page - 1) * pageSize + 1}-
            {Math.min(page * pageSize, totalItems)} de {totalItems}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Anterior
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page >= totalPages}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashRegisterLogsPage;
